import type { KlasseKey, TarifierungKey } from "./types";
import { TARIFIERUNGSARTEN } from "./data";
import type { VersichererKey } from "./VertragsBasisSection";

const VERSICHERER_LABEL: Record<Exclude<VersichererKey, "">, string> = {
  generali: "Generali",
};

export const vereinbarungVersichererLabel = (v: VersichererKey): string =>
  v ? VERSICHERER_LABEL[v] : "—";

export const vereinbarungTarifLabel = (t: TarifierungKey): string =>
  TARIFIERUNGSARTEN.find((x) => x.key === t)?.label ?? t;

/**
 * Mock einer Versicherer-Vereinbarung: pro (Versicherer × Tarifierung)
 * eine Beitragsmatrix Klasse × Deckung/SB. Cells halten Brutto-Beträge in €.
 * HF (Haftpflicht) ist immer mit einem Beitrag belegt; TK und VK/TK enthalten
 * nur die mit dem Versicherer abgestimmten SB-Varianten.
 */
export type VereinbarungRow = {
  klasse: KlasseKey;
  /** column key (SAMMELGRID_COLUMNS.key) → Brutto in € */
  cells: Record<string, number>;
};

export type Vereinbarung = {
  versicherer: VersichererKey;
  tarifierung: TarifierungKey;
  rows: VereinbarungRow[];
};

// Preis-Logik: HF ist der Grundpreis. HF + TK ist immer höher als HF (Kasko
// kommt obendrauf). HF + VK/TK ist immer höher als HF + TK. Innerhalb einer
// Kasko-Spalte fällt die Prämie mit steigender Selbstbeteiligung.
const GENERALI_RAHMENSAMMEL: Vereinbarung = {
  versicherer: "generali",
  tarifierung: "rahmenSammel",
  rows: [
    {
      klasse: "pkw",
      cells: {
        kh: 320,
        "tk:150": 410,
        "tk:500": 380,
        "tk:1000": 360,
        "vktk:150/Ohne": 620,
        "vktk:150/150": 590,
        "vktk:300/150": 555,
        "vktk:500/150": 520,
        "vktk:1000/150": 485,
      },
    },
    {
      klasse: "lieferwagen",
      cells: {
        kh: 540,
        "tk:150": 660,
        "tk:500": 615,
        "vktk:300/Ohne": 920,
        "vktk:500/150": 845,
        "vktk:1000/150": 790,
      },
    },
    {
      klasse: "lkw",
      cells: {
        kh: 820,
        "tk:150": 980,
        "tk:500": 930,
        "tk:1000": 895,
        "vktk:500/500": 1265,
        "vktk:1000/150": 1310,
        "vktk:1000/1000": 1205,
        "vktk:2500/2500": 1095,
      },
    },
  ],
};

const GENERALI_RAHMENSTUECK: Vereinbarung = {
  versicherer: "generali",
  tarifierung: "rahmenStueck",
  rows: [
    {
      klasse: "pkw",
      cells: {
        kh: 360,
        "tk:150": 460,
        "tk:500": 425,
        "tk:1000": 400,
        "vktk:150/Ohne": 690,
        "vktk:300/150": 605,
        "vktk:500/150": 565,
      },
    },
    {
      klasse: "lieferwagen",
      cells: {
        kh: 590,
        "tk:150": 725,
        "tk:500": 670,
        "vktk:500/150": 920,
        "vktk:1000/150": 855,
      },
    },
    {
      klasse: "lkw",
      cells: {
        kh: 890,
        "tk:500": 1015,
        "tk:1000": 970,
        "vktk:1000/150": 1410,
        "vktk:2500/2500": 1180,
      },
    },
    {
      klasse: "anhaenger",
      cells: {
        kh: 220,
        "tk:150": 290,
        "tk:500": 265,
      },
    },
  ],
};

export const VEREINBARUNGEN: Vereinbarung[] = [
  GENERALI_RAHMENSAMMEL,
  GENERALI_RAHMENSTUECK,
];

export const getVereinbarungMock = (
  versicherer: VersichererKey,
  tarifierung: TarifierungKey | "",
): Vereinbarung | null => {
  if (!versicherer || !tarifierung) return null;
  return (
    VEREINBARUNGEN.find(
      (v) => v.versicherer === versicherer && v.tarifierung === tarifierung,
    ) ?? null
  );
};
